import {
  APP_INITIALIZER,
  ApplicationConfig,
  DOCUMENT,
  ErrorHandler,
  PLATFORM_ID,
  importProvidersFrom,
  inject,
  provideAppInitializer,
  provideZoneChangeDetection,
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import {
  HTTP_INTERCEPTORS,
  provideHttpClient,
  withInterceptorsFromDi,
} from '@angular/common/http';
import { Router, provideRouter, withInMemoryScrolling } from '@angular/router';
import { provideClientHydration, withEventReplay } from '@angular/platform-browser';
import { provideAnimations } from '@angular/platform-browser/animations';

import { TranslateLoader, TranslateModule, TranslationObject } from '@ngx-translate/core';
import { from, Observable } from 'rxjs';
import { ToastrModule } from 'ngx-toastr';
import { JwtModule } from '@auth0/angular-jwt';
import { NgcCookieConsentConfig, NgcCookieConsentModule } from 'ngx-cookieconsent';
import { NGX_GOOGLE_ANALYTICS_SETTINGS_TOKEN, NGX_GTAG_FN } from 'ngx-google-analytics';
import * as Sentry from '@sentry/angular';

import { appRoutes } from './app.routes';
import { ErrorInterceptor } from './core/interceptors/error.interceptor';
import { environment } from '../environments/environment';

const cookieConfig: NgcCookieConsentConfig = {
  cookie: {
    domain: environment.clientUrl,
  },
  palette: {
    popup: { background: '#000' },
    button: { background: '#f1d600' },
  },
  theme: 'edgeless',
  type: 'opt-out',
};

// Translations are bundled, one content-hashed chunk per language, so a release's labels arrive with its code.
// Fetched from assets/i18n they were cached for a week under the same URL, and new menu items showed their keys.
const TRANSLATIONS: Record<string, () => Promise<{ default: TranslationObject }>> = {
  en: () => import('../assets/i18n/en.json'),
  es: () => import('../assets/i18n/es.json'),
  de: () => import('../assets/i18n/de.json'),
  it: () => import('../assets/i18n/it.json'),
  ru: () => import('../assets/i18n/ru.json'),
};

export class BundledTranslations implements TranslateLoader {
  getTranslation(lang: string): Observable<TranslationObject> {
    return from((TRANSLATIONS[lang] ?? TRANSLATIONS['en'])().then(m => m.default));
  }
}

export function tokenGetter() {
  return typeof localStorage !== 'undefined' ? localStorage.getItem('token') : null;
}

const GA_TRACKING_CODE = 'G-C9FPGJX6X5';

// What NgxGoogleAnalyticsModule.forRoot() sets up, with two changes: the gtag script is only added in
// the browser (added while prerendering, it would be baked into the static HTML and load twice), and
// only once the page has loaded, so it doesn't compete with the first paint. The init commands go into
// the dataLayer right away, so page views sent before the script arrives are queued behind them.
const analyticsProviders = environment.production
  ? [
    { provide: NGX_GOOGLE_ANALYTICS_SETTINGS_TOKEN, useValue: { trackingCode: GA_TRACKING_CODE } },
    provideAppInitializer(() => {
      if (!isPlatformBrowser(inject(PLATFORM_ID))) {
        return;
      }
      // The library types gtag() too narrowly for the 'js' command, which takes a Date.
      const gtag = inject(NGX_GTAG_FN) as (...args: unknown[]) => void;
      const document = inject(DOCUMENT);
      gtag('js', new Date());
      gtag('config', GA_TRACKING_CODE);

      const loadScript = () => {
        const script = document.createElement('script');
        script.async = true;
        script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_TRACKING_CODE}`;
        document.head.appendChild(script);
      };
      if (document.readyState === 'complete') {
        loadScript();
      } else {
        window.addEventListener('load', loadScript, { once: true });
      }
    }),
  ]
  : [];

export const appConfig: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection(),
    // Reuses the prerendered DOM of the public pages instead of re-rendering it (no flash, better LCP/CLS).
    provideClientHydration(withEventReplay()),
    provideAnimations(),
    provideRouter(appRoutes, withInMemoryScrolling({ scrollPositionRestoration: 'top', anchorScrolling: 'enabled' })),
    provideHttpClient(withInterceptorsFromDi()),
    importProvidersFrom(
      TranslateModule.forRoot({
        loader: {
          provide: TranslateLoader,
          useClass: BundledTranslations,
        },
      }),
      ToastrModule.forRoot(),
      JwtModule.forRoot({
        config: {
          tokenGetter,
          allowedDomains: [environment.jwtDomain],
          disallowedRoutes: [],
        },
      }),
      NgcCookieConsentModule.forRoot(cookieConfig),
    ),
    ...analyticsProviders,
    { provide: HTTP_INTERCEPTORS, useClass: ErrorInterceptor, multi: true },
    {
      provide: ErrorHandler,
      useValue: Sentry.createErrorHandler({
        showDialog: environment.sentry.showDialog,
      }),
    },
    {
      provide: Sentry.TraceService,
      deps: [Router],
    },
    {
      provide: APP_INITIALIZER,
      useFactory: () => () => {},
      deps: [Sentry.TraceService],
      multi: true,
    },
  ],
};
